import type { TFunction } from 'i18next';
import { difference, map, toLower } from 'lodash-es';
import type { ReactElement } from 'react';

import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Grid, MenuItem, Typography, styled } from '@mui/material';

import { getOpenOptionsFromBottom } from 'common/utils/menu';
import { required } from 'common/utils/validations';
import { CIDropDown } from 'common/viewcomp/forms/fields/select';
import { CITextField } from 'common/viewcomp/forms/fields/text';

import { useSecurityQuestions } from './hooks/useSecurityQuestions';
import type { QuestionIndex } from './questionIndexEnum';

interface QuestionAnswerFormFieldProps {
    questionIndex: QuestionIndex;
    selectedQuestions: string[];
    t: TFunction;
    labelI18NKey?: string;
    isActivationFlow?: boolean;
    onChange: () => void;
}

export const QuestionAnswerFormField = ({
    questionIndex,
    t,
    selectedQuestions,
    labelI18NKey = '',
    isActivationFlow = false,
    onChange,
}: QuestionAnswerFormFieldProps) => {
    const { securityQuestions } = useSecurityQuestions(t);

    const validationProps = isActivationFlow
        ? {
              required: true,
              validations: [required('error.required')],
          }
        : {};

    return (
        <Grid container spacing={isActivationFlow ? 0 : 2} tabIndex={0} style={{ marginBottom: '8px' }}>
            <Grid item lg={isActivationFlow ? 12 : 6} md={12}>
                <DropDown
                    id={`question${questionIndex}`}
                    name={`question${questionIndex}`}
                    t={t}
                    displayEmpty
                    labelI18NKey={labelI18NKey}
                    ariaLabel={labelI18NKey}
                    margin="normal"
                    variant="outlined"
                    fullWidth
                    labelId="question-dropdown-label"
                    iconComponent={ExpandMoreIcon}
                    menuProps={getOpenOptionsFromBottom()}
                    renderValue={(selected): ReactElement => {
                        if (selected.length === 0 && !isActivationFlow) {
                            return <em> {t(`security_questions.selectQuestionDefaultText`)}</em>;
                        }

                        return selected;
                    }}
                    onChange={onChange}
                    {...validationProps}
                >
                    {map(
                        difference(securityQuestions, selectedQuestions),
                        (question, index): ReactElement => (
                            <MenuItem key={index} value={question}>
                                <QuestionText question={question} />
                            </MenuItem>
                        )
                    )}
                </DropDown>
            </Grid>
            <Grid item lg={isActivationFlow ? 12 : 6} md={12}>
                <AnswerTextField
                    t={t}
                    labelI18NKey={t(`security_questions.answer_text`)}
                    id={`question${questionIndex}Answer`}
                    name={`question${questionIndex}Answer`}
                    variant="outlined"
                    type="text"
                    fullWidth
                    margin="normal"
                    validations={[required()]}
                    required
                    ariaLabel={t(`security_questions.${toLower(`question${questionIndex}Answer`)}_label`)}
                />
            </Grid>
        </Grid>
    );
};

const QuestionText = ({ question }: { question: string }): ReactElement => (
    <QuestionTypography noWrap variant="inherit">
        {question}
    </QuestionTypography>
);

const QuestionTypography = styled(Typography)({
    direction: 'ltr',
    whiteSpace: 'normal',
    '.rtl &': {
        direction: 'rtl',
        whiteSpace: 'normal',
    },
});

const DropDown = styled(CIDropDown)(({ theme }) => ({
    '& .MuiSelect-select': {
        background: theme.ciPalette.background.fullWhite,
    },
    '& .MuiInputLabel-shrink': {
        background: theme.ciPalette.background.fullWhite,
        paddingLeft: '5px',
        paddingRight: '5px',
        '.rtl &': {
            transform: 'translate(-14px, -6px) scale(0.75)',
        },
    },
}));

const AnswerTextField = styled(CITextField)(({ theme }) => ({
    '& .MuiInputBase-input': {
        background: theme.ciPalette.background.fullWhite,
    },
}));
